import { get, uniq } from "lodash";
import { useMemo } from "react";
import format from "string-template";
import { useQuery } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { resolveProductName } from "utils/helpers/resolveProductName";
import { resolveQuoteDocumentVersion } from "utils/helpers/resolveQuoteDocumentVersion";
import Card from "./Card";

const AURA_TER_PRODUCT_NAME =
  "Ma mutuelle Région Auvergne-Rhône-Alpes Agents Territoriaux";

const EmailTemplate = ({
  civility,
  lastName,
  firstName,
  adminPhoneNumber,
  adminEmail,
  adminFirstName,
  adminLastName,
  translation,
  costs,
  addLinks,
  organizationId,
  tracer,
}) => {
  const productCodes = useMemo(
    () =>
      uniq(
        (costs || [])
          .map((cost) => get(cost, "insurancePlan.product"))
          .filter(Boolean),
      ),
    [costs],
  );

  const isAuraTerTracer = get(tracer, "destination") === "AURA_TER";

  const shouldResolveProductName =
    !isAuraTerTracer && productCodes.length > 0 && !!organizationId;

  const { data: productRangesData, loading: productRangesLoading } = useQuery(
    graphql.queries.PRODUCT_RANGES,
    {
      variables: {
        where: { ownedBy: { id: organizationId } },
        take: 500,
        orderBy: [{ code: "ASC" }],
      },
      skip: !shouldResolveProductName,
    },
  );

  const isProductNameReady = !shouldResolveProductName || !productRangesLoading;

  const offerName = useMemo(() => {
    if (isAuraTerTracer) return AURA_TER_PRODUCT_NAME;
    if (!isProductNameReady) return "";

    return resolveProductName({
      productCodes,
      productRanges: get(productRangesData, "productRanges.data", []),
    });
  }, [isAuraTerTracer, isProductNameReady, productCodes, productRangesData]);

  const documentVersion = useMemo(
    () =>
      resolveQuoteDocumentVersion(costs, { auraTerTracer: isAuraTerTracer }),
    [costs, isAuraTerTracer],
  );

  const attachedDocumentsBlockKey =
    documentVersion === "AURA_TERR" ? "AURA_TERR" : "DEFAULT";

  const { documentsTitle, documents } = useMemo(() => {
    const block = get(
      translation,
      `ATTACHED_DOCUMENTS.${attachedDocumentsBlockKey}`,
      {},
    );
    const items = get(block, "ITEMS", []);

    return {
      documentsTitle: get(block, "TITLE", ""),
      documents: items,
    };
  }, [translation, attachedDocumentsBlockKey]);

  const documentsMidpoint = Math.ceil(documents.length / 2);
  const leftDocuments = documents.slice(0, documentsMidpoint);
  const rightDocuments = documents.slice(documentsMidpoint);

  return (
    <div className="quote-template-miltis__main-container">
      <div className="quote-template-miltis__header">
        <img
          src="https://assets.kayros.tech/miltis/logo.png"
          alt="Miltis Mutuelle Logo"
        />
      </div>
      <div className="quote-template-miltis__container">
        <div className="quote-template-miltis__content">
          <p>
            {translation.HELLO} {translation[civility] || civility} {firstName}{" "}
            {lastName},
          </p>
          {isProductNameReady ? (
            <p
              dangerouslySetInnerHTML={{
                __html: format(translation.P1, { offerName }),
              }}
            />
          ) : null}
        </div>
        <div className="quote-template-miltis__formulas">
          {costs?.map(({ id, insurancePlan, cost, currency }, index) => (
            <Card
              key={id}
              insurancePlan={insurancePlan}
              cost={cost}
              currency={currency}
              translation={translation}
              index={index}
              addLinks={addLinks}
            />
          ))}
        </div>
        <div className="quote-template-miltis__content">
          <p>
            <b>{documentsTitle}</b>
          </p>
        </div>
        <div className="quote-template-miltis__content">
          <div className="quote-template-miltis__documents">
            <div className="quote-template-miltis__documents--column">
              {leftDocuments.map((doc, index) => (
                <div key={index} className="quote-template-miltis__document">
                  <span className="quote-template-miltis__document--icon">
                    <img
                      src="https://img.icons8.com/parakeet-line/16/document.png"
                      alt="Document Icon"
                    />
                  </span>
                  <span className="quote-template-miltis__document--link">
                    {doc}
                  </span>
                </div>
              ))}
            </div>
            <div className="quote-template-miltis__documents--column">
              {rightDocuments.map((doc, index) => (
                <div
                  key={documentsMidpoint + index}
                  className="quote-template-miltis__document"
                >
                  <span className="quote-template-miltis__document--icon">
                    <img
                      src="https://img.icons8.com/parakeet-line/16/document.png"
                      alt="Document Icon"
                    />
                  </span>
                  <span className="quote-template-miltis__document--link">
                    {doc}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="quote-template-miltis__content">
          <p>{translation.TERMINATION_INFO}</p>
        </div>
        <div className="quote-template-miltis__content">
          <p
            dangerouslySetInnerHTML={{
              __html: format(translation.CONTACT_INFO, {
                phoneNumber: adminPhoneNumber,
                email: adminEmail,
              }),
            }}
          />
        </div>
        <div className="quote-template-miltis__content">
          <p>{translation.CLOSING}</p>
        </div>
        <div className="quote-template-miltis__info">
          <p className="quote-template-miltis__info--text">
            {translation.YOUR_ADVISOR}
          </p>
          <p className="quote-template-miltis__info--name">
            {adminFirstName} {adminLastName}
          </p>
          <p>{adminEmail}</p>
          <p>{adminPhoneNumber}</p>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplate;
